import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import SwiperCore, { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Tooltip from '../Tooltip/Tooltip';
import QuestionIcon from '../Icons/QuestionIcon';
import TrackingService from '@/services/tracking';
import { Box, HStack, IconButton, Text } from '@design-system/pc';

interface GridListBoxProps<T extends {}> {
  title?: string;
  imgList?: any;
  gridNum: number;
  colNum?: number;
  isAmount?: boolean;
  isTooltip?: boolean;
  isArrow?: boolean;
  disabled?: boolean;
  spaceBetween?: number;
  tooltip?: {
    title: string;
    text: string;
  };
  amountNum?: number;
  children: (args: { item: T; index: number }) => ReactNode | ReactNode[];
  gtmName?: string;
  gtmValue?: Record<string, any>;
}

const GridListBox = <T extends {}>({
  title,
  imgList,
  gridNum = 1,
  colNum = 1,
  isAmount = false,
  isTooltip = false,
  isArrow = true,
  spaceBetween = 20,
  amountNum,
  disabled = false,
  tooltip,
  gtmName = '',
  gtmValue,
  children,
}: GridListBoxProps<T>) => {
  const [swiper, setSwiper] = useState<SwiperCore>();
  return (
    <Container {...TrackingService.tagGTM(`${gtmName}`)}>
      <HeaderContainer alignment="center">
        <HeaderTitleContainer>
          <HeaderTitle variant="subTitle4">{title}</HeaderTitle>
          {isAmount && (
            <HeaderPages variant="subTitle4">
              ({amountNum || imgList?.length})
            </HeaderPages>
          )}
          {isTooltip && (
            <Tooltip
              title={tooltip?.title}
              text={tooltip?.text}
              gtmName={gtmName}
            >
              <QuestionIcon boxSize={20} />
            </Tooltip>
          )}
        </HeaderTitleContainer>
        {isArrow && (
          <HStack space={8}>
            <IconBox
              disabled={disabled}
              {...TrackingService.tagGTM(`${gtmName}_prev_button`, gtmValue)}
            >
              <IconButton
                size="sm"
                variant="ic_arrow_left_bold"
                useFillColor={true}
                fillColor={
                  disabled ? 'color_chip.gray.500' : 'color_chip.primary'
                }
                onClick={() => swiper?.slidePrev()}
              />
            </IconBox>
            <IconBox
              disabled={disabled}
              {...TrackingService.tagGTM(`${gtmName}_next_button`, gtmValue)}
            >
              <IconButton
                size="sm"
                variant="ic_arrow_right_bold"
                useFillColor={true}
                fillColor={
                  disabled
                    ? 'color_chip.gray.500'
                    : 'color_chip.primary.default'
                }
                onClick={() => swiper?.slideNext()}
              />
            </IconBox>
          </HStack>
        )}
      </HeaderContainer>
      <Swiper
        modules={[Navigation]}
        onSwiper={setSwiper}
        navigation={true}
        spaceBetween={spaceBetween}
        slidesPerGroup={gridNum}
        slidesPerView={gridNum}
        {...TrackingService.tagGTM(`${gtmName}_list`, gtmValue)}
      >
        {imgList?.map((item: any, index: number) => (
          <SwiperSlide key={`slide-box-${index + 1}`} style={{ gap: '20px' }}>
            {children({
              item,
              index,
            })}
          </SwiperSlide>
        ))}
      </Swiper>
    </Container>
  );
};

export default GridListBox;

const Container = styled(Box)`
  width: 100%;
  margin-bottom: 80px;
  :last-child {
    margin-bottom: 0px;
  }
`;

const HeaderContainer = styled(HStack)`
  width: 100%;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const HeaderTitleContainer = styled(HStack)`
  align-items: center;
  gap: 4px;
`;

const HeaderTitle = styled(Text)``;

const HeaderPages = styled(Text)``;

const IconBox = styled(Box)<{ disabled: boolean }>`
  width: fit-content;
  height: fit-content;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`;
